// Packages and components
import React, { useEffect, useRef, useState } from 'react';
import { Box, Drop } from 'grommet';
import { Clock } from 'grommet-icons';
import SHDSpinner from '@ui/Spinner/SHDSpinner';

// Helpers and utlities
import {
  padNumber,
  formatTimeInputValue,
  formatHours,
  normalizeTimeToStep,
} from '@theme/utils/data';

// Styles
import { StyledSHDInput } from './SHDInputTime.styles';

// Types
import type { SelectOption, Meridiem } from '@theme/theme.d';
import type { SHDInputTimeProps } from './SHDInputTime.d';
import { DateTime } from 'luxon';

const SHDInputTime: React.FC<SHDInputTimeProps> = ({
  className = 'input-time',
  error = null,
  label = '',
  minuteStep = 5,
  showIcon = false,
  size = 'small',
  onChange,
  disabled = false,
  hours = null,
  minutes = null,
}) => {
  // States
  const now = DateTime.now();
  const [showDrop, setShowDrop] = useState<boolean>(false);
  const baseHour = hours === null ? now.hour : hours;
  const baseMinute =
    minutes === null ? now.plus({ minutes: 5 }).minute : minutes;

  const normalized = normalizeTimeToStep(baseHour, baseMinute, minuteStep);

  const [hour, setHour] = useState<number>(normalized.hour);
  const [minute, setMinute] = useState<number>(normalized.minute);
  const [meridiem, setMeridiem] = useState<Meridiem>(hour < 12 ? 'AM' : 'PM');
  const [timeObj, setTimeObj] = useState<SelectOption>({
    label: `${hour}:${minute} ${meridiem}`,
    value: `${hour}:${minute} ${meridiem}`,
  });

  // Refs
  const inputRef = useRef(null);

  // Handlers
  const handleHourChangeUp = () => {
    setHour((prevHour) => {
      if (prevHour === 11) {
        setMeridiem('PM');
        return 12;
      }
      if (prevHour === 23) {
        setMeridiem('AM');
        return 0;
      }
      return prevHour + 1;
    });
  };

  const handleHourChangeDown = () => {
    setHour((prevHour) => {
      if (prevHour === 12) {
        setMeridiem('AM');
        return 11;
      }
      if (prevHour === 0) {
        setMeridiem('PM');
        return 23;
      }
      return prevHour - 1;
    });
  };

  const handleMinuteChangeUp = () => {
    setMinute((prevMin) => {
      if (prevMin === 60 - minuteStep || prevMin > 60) {
        handleHourChangeUp();
        return 0;
      }
      return prevMin + minuteStep;
    });
  };

  const handleMinuteChangeDown = () => {
    setMinute((prevMin) => {
      if (prevMin === 0 || prevMin < 0) {
        handleHourChangeDown();
        return 60 - minuteStep;
      }
      return prevMin - minuteStep;
    });
  };

  const handleMeridiemChange = () => {
    setMeridiem((prevMer) => {
      if (prevMer === 'AM') {
        return handleMeridiemChangeAmToPm();
      }
      return handleMeridiemChangePmToAm();
    });
  };

  const handleMeridiemChangeAmToPm = (): Meridiem => {
    setHour((prevHour) => {
      if (prevHour === 12) {
        return 0;
      }
      if (prevHour === 0) {
        return 12;
      }
      return prevHour + 12;
    });
    return 'PM';
  };

  const handleMeridiemChangePmToAm = (): Meridiem => {
    setHour((prevHour) => {
      if (prevHour === 12) {
        return 0;
      }
      if (prevHour === 0) {
        return 12;
      }
      return prevHour - 12;
    });
    return 'AM';
  };

  const handleCloseDrop = () => {
    setShowDrop(false);
    onChange(timeObj);
  };

  // Effects
  useEffect(() => {
    const newTime: SelectOption = formatTimeInputValue(hour, minute, meridiem);
    setTimeObj(newTime);
  }, [hour, minute, meridiem]);

  return (
    <div className={className} data-testid="input-time">
      <div ref={inputRef} className={`${className}__input`}>
        <StyledSHDInput
          data-testid="input-time-field"
          className={`${className}__input`}
          disabled={disabled}
          icon={
            showIcon ? (
              <Clock style={{ marginRight: '16px' }} />
            ) : (
              <Clock color="transparent" />
            )
          }
          error={error}
          label={label}
          readOnly
          reverse
          value={timeObj.label}
          width="100%"
          onFocus={() => setShowDrop(true)}
        />
      </div>
      {showDrop && (
        <Drop
          align={{ top: 'bottom', left: 'left' }}
          background="white"
          role="dialog"
          round="xxsmall"
          stretch={false}
          target={inputRef.current}
          onClickOutside={() => handleCloseDrop()}
          onEsc={() => handleCloseDrop()}
          width={size}
        >
          <Box
            direction="row"
            gap="small"
            alignContent="center"
            justify="between"
            pad="medium"
          >
            <SHDSpinner
              data-testid="input-time-spinner"
              size={size}
              value={padNumber(formatHours(hour), 2)}
              onChangeUp={() => handleHourChangeUp()}
              onChangeDown={() => handleHourChangeDown()}
            />
            <SHDSpinner
              data-testid="input-time-spinner"
              size={size}
              value={padNumber(minute, 2)}
              onChangeUp={() => handleMinuteChangeUp()}
              onChangeDown={() => handleMinuteChangeDown()}
            />
            <SHDSpinner
              data-testid="input-time-spinner"
              size={size}
              value={meridiem}
              onChangeUp={() => handleMeridiemChange()}
              onChangeDown={() => handleMeridiemChange()}
            />
          </Box>
        </Drop>
      )}
    </div>
  );
};

export default SHDInputTime;
